/// <reference types="vite/client" />
import { Mark, mergeAttributes } from '@tiptap/core';
import { CommandProps } from '@tiptap/core';
import { router } from '@inertiajs/react';
import { twMerge } from 'tailwind-merge';
import { clsx } from 'clsx';
// Vite provides import.meta.env typings automatically; no need to redeclare ImportMeta or ImportMetaEnv.

export function cn(...inputs: any[]): string {
    return twMerge(clsx(inputs));
}

export const getFirstLetter = (text: string): string => {
    return (
        text
            .toUpperCase()
            .match(/\b(\w)/g)
            ?.join('')
            ?.slice(0, 2) ?? text[0]
    );
};

export function visitPartial(
    url: string,
    params: {
        only?: string[];
        headers?: Record<string, string>;
        [key: string]: any;
    }
): void {
    const only = params.only || [];
    const paramsWithDot = only.filter((item) => item.includes('.'));

    if (paramsWithDot.length > 0) {
        params.only = only.filter((item) => !item.includes('.'));
        const additional = [...new Set(paramsWithDot.map((item) => item.split('.')[0]))];

        params.only = [
            ...params.only,
            ...additional,
            'flash',
            'errors',
            'info_popup',
            'csrf_token',
            'alerts',
        ];

        params.headers = {
            'X-Inertia-Partial-Data-Dot': paramsWithDot.join(','),
        };
    }

    router.visit(url, {
        ...params,
    });
}

export function debounce<T extends (...args: any[]) => void>(
    func: T,
    ms: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>;
    return function (...args: Parameters<T>): void {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), ms);
    };
}

export const GRId = (length: number = 5): string => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

export function campaignAssetUrl(url: string): string {
    return url.startsWith('data:image') ||
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('//')
        ? url
        : url.startsWith('/storage')
          ? `${import.meta.env.VITE_CDN_URL}${url.replace('/storage', '')}`
          : `${import.meta.env.VITE_CDN_URL}${url}`;
}

export function assetUrl(url: string): string {
    return url.startsWith('http://') ||
        url.startsWith('https://') ||
        !import.meta.env.VITE_ASSET_URL
        ? url
        : `${import.meta.env.VITE_ASSET_URL}/${url.replace(/^\//, '')}`;
}

export const convertBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
            resolve(fileReader.result);
        };
        fileReader.onerror = (error) => {
            reject(error);
        };
    });
};

type SentryEvent = {
    exception?: {
        values?: Array<{
            value?: string;
        }>;
    };
    [key: string]: any;
};

export const sentryBeforeSend = (event: SentryEvent, hint: object): any | null => {
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT === 'local') {
        console.error('Sentry error', { event, hint });
        return null;
    }

    const ignoredMessages = [
        "Cannot read properties of null (reading 'scrollRegions')",
        'Failed to fetch',
        "Cannot read properties of null (reading 'page')",
        'window.history.state.scrollRegions',
        'Non-Error promise rejection captured with value: Timeout',
        "Failed to execute 'insertBefore' on 'Node'",
        "Cannot read properties of undefined (reading 'version')",
        'NetworkError',
        'Unable to preload CSS',
        "Failed to execute 'setEnd' on 'Range'",
        'window.webkit.messageHandlers[t].postMessage',
        "(reading 'shadowRoot')",
        'No error message',
        'Load failed',
        'out of memory',
        'Request aborted',
        'Event `CustomEvent`',
        'Network Error',
        "Failed to execute 'collapse' on 'Selection'",
        'window.webkit.messageHandlers',
        'Non-Error promise rejection captured',
        '__ is not defined',
        "Failed to execute 'removeChild' on 'Node'",
        'Object.hasOwn is not a function',
        'WKWebView API client did not respond to this postMessage',
        'Java object is gone',
        'timeout exceeded',
        "Unexpected token 'else'",
        "Cannot read properties of undefined (reading '__possible_keys')",
        'document.querySelector(\'button[aria-label*="search"]\').addEventListener',
        'Chartboost is not defined',
        "Can't find variable: Chartboost",
        '$a',
    ];

    if (
        event?.exception?.values?.[0]?.value &&
        ignoredMessages.some((msg) => event.exception?.values?.[0]?.value?.includes(msg))
    ) {
        return null;
    }

    return event;
};

export interface VariableOptions {
    HTMLAttributes: Record<string, any>;
}
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        variable: {
            insertVariable: (name: string) => ReturnType;
        };
    }
}
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        variable: {
            insertVariable: (name: string) => ReturnType;
        };
    }
}

type VariableAttributes = {
    name: string | null;
};

export const Variable = Mark.create<VariableAttributes>({
    name: 'variable',

    addAttributes() {
        return {
            name: {
                default: null,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-variable]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-variable': HTMLAttributes.name,
                class: 'variable-token',
            }),
        ];
    },

    addCommands() {
        return {
            insertVariable:
                (name: string) =>
                ({ chain }: CommandProps) => {
                    return chain()
                        .setMark(this.name, { name })
                        .insertContent(`{${name}}`)
                        .run();
                },
        };
    },
});

export function fromDateToString(backendDate: string | number | Date): string {
    const date = new Date(backendDate);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function isDark(hex: string): boolean {
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}
